import { useCallback, useRef, useState } from "react";
import { useController, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, CircularProgress, FormControl, FormLabel, InputAdornment, TextField } from "@mui/material";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import { DEFAULT_GROUP_ID, SPACE_BYPASS_2FA } from "@/configs/runtime";
import { LOGIN_FIELD_LABEL_CLASSNAME, LOGIN_TEXT_FIELD_CLASSNAME } from "../loginForm.constants";
import { valoresPadraoEntrar, type FormularioEntrarType, schemaEntrar } from "./loginGrupoFixo.schema";
import { deveContinuarEntradaAposSelecionarUsuario, deveUsarFluxoOtpDireto } from "./utils/loginGrupoFixo.utils";
import type { ISpaceAuthLookupResponse } from "@/api/space/dtos/authenticator.dto";
import type { IntencaoBuscaUsuario } from "./loginGrupoFixo.types";
import useFluxoLoginGrupoFixo from "./hooks/useFluxoLoginGrupoFixo";
import useSelecaoUsuarioLoginGrupoFixo from "./hooks/useSelecaoUsuarioLoginGrupoFixo";
import SecaoSenhaEntrarJwt from "./ui/SecaoSenhaEntrarJwt";
import DialogoSelecaoUsuario from "../../SelecaoUsuario/DialogoSelecaoUsuario";
import DialogoOtpGrupoFixo from "./ui/dialogs/DialogoOtpGrupoFixo";

// ------------------------------- COMPONENT -------------------------------
export default function FormularioEntrarGrupoFixoJwt() {
	// -------------------------------- STATES --------------------------------
	const [showPassword, setShowPassword] = useState(false);
	const passwordRef = useRef<HTMLInputElement>(null);
	const usernameRef = useRef<HTMLInputElement>(null);
	const submitRef = useRef<() => Promise<void>>(() => Promise.resolve());
	const groupId = DEFAULT_GROUP_ID ?? "";

	// --------------------------------- FORM ---------------------------------
	const { control, formState, handleSubmit, setError, setValue, clearErrors } = useForm<FormularioEntrarType>({
		mode: "onChange",
		defaultValues: valoresPadraoEntrar,
		resolver: zodResolver(schemaEntrar),
	});
	const { field: usernameField, fieldState: usernameFieldState } = useController({ control, name: "username" });
	const { field: passwordField } = useController({ control, name: "password" });
	const username = usernameField.value;
	const aoAlterarCampoUsuario = usernameField.onChange;

	// -------------------------------- HANDLERS ------------------------------
	// A seleção usa estes callbacks em um effect; eles preservam a identidade dos efeitos.
	const alterarCampoUsuario = useCallback(
		(nome: string) => {
			aoAlterarCampoUsuario(nome);
		},
		[aoAlterarCampoUsuario],
	);

	const aplicarSelecao = useCallback(
		(usuario: ISpaceAuthLookupResponse, intencao: IntencaoBuscaUsuario) => {
			clearErrors("username");
			const otpDireto = deveUsarFluxoOtpDireto(usuario, SPACE_BYPASS_2FA);
			if (otpDireto) {
				setValue("password", "", { shouldDirty: false, shouldValidate: true });
				setShowPassword(false);
				if (deveContinuarEntradaAposSelecionarUsuario(intencao, otpDireto))
					globalThis.setTimeout(() => {
						void submitRef.current();
					}, 0);
				return;
			}
			globalThis.setTimeout(() => passwordRef.current?.focus(), 0);
		},
		[clearErrors, setValue],
	);

	const selecao = useSelecaoUsuarioLoginGrupoFixo({
		username,
		groupId,
		aoAlterarUsuario: alterarCampoUsuario,
		aoSelecionar: aplicarSelecao,
		aoEntrar: () => {
			void submitRef.current();
		},
	});
	const fluxo = useFluxoLoginGrupoFixo({
		groupId,
		aoErro: (campo, mensagem) => setError(campo, { type: "manual", message: mensagem }),
	});

	async function submeter(dados: FormularioEntrarType): Promise<void> {
		if (selecao.mustChoose) {
			setError("username", { type: "manual", message: "Selecione o usuário correto na lista." });
			return;
		}
		await fluxo.entrar(dados, selecao.obterUsuarioSelecionado());
	}
	const submitHandler = handleSubmit(submeter);
	submitRef.current = submitHandler;

	function fecharOtp(): void {
		fluxo.cancelarOtp();
		globalThis.setTimeout(() => usernameRef.current?.focus(), 100);
	}

	const togglePasswordVisibility = useCallback(() => setShowPassword((prev) => !prev), []);

	// ----------------------------- DERIVED STATE ----------------------------
	const needsPassword = !selecao.isDirectOtpFlow && !passwordField.value?.trim();
	const isSubmitDisabled =
		!groupId ||
		!formState.isDirty ||
		!formState.isValid ||
		selecao.mustChoose ||
		fluxo.isSigningIn ||
		needsPassword;

	// -------------------------------- RETURN --------------------------------
	return (
		<>
			<form
				name="loginForm"
				noValidate
				className="flex w-full flex-col justify-center space-y-4"
				onSubmit={submitHandler}
			>
				<FormControl fullWidth className="space-y-1.5">
					<FormLabel htmlFor="username" className={LOGIN_FIELD_LABEL_CLASSNAME}>
						Usuário
					</FormLabel>
					<TextField
						id="username"
						size="small"
						className={LOGIN_TEXT_FIELD_CLASSNAME}
						value={usernameField.value}
						placeholder="Digite usuário ou ID"
						error={!!usernameFieldState.error || selecao.mustChoose}
						helperText={
							usernameFieldState.error?.message ||
							(selecao.mustChoose ? "Existem múltiplos usuários. Selecione." : " ")
						}
						onChange={(evento) => selecao.alterarUsuario(evento.target.value)}
						onBlur={usernameField.onBlur}
						onKeyDown={selecao.tratarTecla}
						slotProps={{
							input: {
								autoComplete: "username",
								startAdornment: (
									<InputAdornment position="start">
										<FuseSvgIcon className="text-[#99aabb]" size={16}>
											lucide:circle-user-round
										</FuseSvgIcon>
									</InputAdornment>
								),
								endAdornment: selecao.loadingUsers ? (
									<CircularProgress size={18} className="text-[#504a99]" />
								) : undefined,
							},
						}}
						inputRef={usernameRef}
						autoFocus
					/>
				</FormControl>
				<SecaoSenhaEntrarJwt
					control={control}
					exibirSenha={showPassword}
					aoAlternarVisibilidade={togglePasswordVisibility}
					referenciaSenha={passwordRef}
					requerDoisFatores={selecao.isDirectOtpFlow}
				/>
				{formState.errors.root?.message ? (
					<Alert severity="error">{formState.errors.root.message}</Alert>
				) : null}
				<button
					className="relative mt-2 h-11.5 hover:cursor-pointer w-full overflow-hidden rounded-[10px] border-0 bg-[linear-gradient(135deg,#e6215a_0%,#514b9a_60%,#504a99_100%)] text-[14px] font-bold tracking-[0.06em] text-white uppercase shadow-[0_6px_20px_rgba(230,33,90,.35)] transition-[transform,box-shadow,opacity] duration-150 before:pointer-events-none before:absolute before:inset-0 before:rounded-[10px] before:bg-[linear-gradient(135deg,rgba(255,255,255,.18),transparent)] before:content-[''] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:shadow-[0_6px_20px_rgba(230,33,90,.35)] hover:opacity-85"
					aria-label="Entrar no sistema"
					disabled={isSubmitDisabled}
					type="submit"
				>
					<span className="relative z-10 flex h-full items-center justify-center">
						{fluxo.isSigningIn ? <CircularProgress size={20} color="inherit" /> : "Entrar no Sistema"}
					</span>
				</button>
			</form>
			<DialogoSelecaoUsuario
				aberto={selecao.dialogAberto}
				termoBusca={username}
				usuarios={selecao.lookupEnabled ? selecao.options : []}
				carregando={selecao.lookupEnabled && selecao.loadingUsers}
				erro={selecao.lookupEnabled && selecao.lookupError}
				aoAlterarBusca={selecao.alterarUsuario}
				aoSelecionar={selecao.selecionarNoDialog}
				aoFechar={selecao.fecharDialog}
			/>
			{fluxo.pendente ? (
				<DialogoOtpGrupoFixo pendente={fluxo.pendente} aoFechar={fecharOtp} aoConcluir={fluxo.concluirOtp} />
			) : null}
		</>
	);
}
